import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/shared/components/buttons';

export type StripeLinkContext = 'user_page' | 'organization_page';

export interface StripeSubscriptionSummary {
  id: string;
  status: string;
  price_id: string | null;
  price_nickname: string | null;
  current_period_end: string | null;
}

export interface StripeLinkedUser {
  id: number;
  email: string;
  admin_url: string;
}

export interface StripeCustomerSummary {
  id: string;
  email: string | null;
  name: string | null;
  created_at: string | null;
  dashboard_url: string;
  subscriptions: StripeSubscriptionSummary[];
  linked_user: StripeLinkedUser | null;
  current: boolean;
}

interface StripeCustomerLinkerProps {
  userId: number;
  userEmail: string;
  context: StripeLinkContext;
  /** GET, receives ?q=&user_id= */
  searchUrl: string;
  /** GET, receives ?user_id= */
  currentUrl: string;
  /** POST { user_id, customer_id, context } */
  linkUrl: string;
  csrfToken: string;
}

type JsonRecord = Record<string, unknown>;

interface JsonResult {
  ok: boolean;
  status: number;
  redirected: boolean;
  data: JsonRecord | null;
}

type CurrentState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; stripeId: string | null; customer: StripeCustomerSummary | null; note: string | null };

interface LinkFeedback {
  customerId: string;
  variant: 'success' | 'danger';
  text: string;
}

/**
 * Where keyboard focus goes after the control that held it unmounts:
 * 'confirm' is the inline confirm panel, 'link' the result's Link button,
 * 'feedback' the result's success or error message.
 */
type FocusTarget = 'confirm' | 'link' | 'feedback';

interface FocusRequest {
  customerId: string;
  target: FocusTarget;
}

const DASHBOARD_PREFIX = 'https://dashboard.stripe.com/';
const BLANK_QUERY_MESSAGE = 'Enter an email, name, or customer ID';
const NETWORK_MESSAGE = 'Could not reach the server. Check your connection and try again.';
const SESSION_MESSAGE = 'Your session has expired. Reload the page and sign in again.';
const FORBIDDEN_MESSAGE = 'You do not have billing access for this action.';
const REDIRECT_MESSAGE =
  'The server redirected this request, which usually means your session expired or you lack billing access. Reload the page and try again.';

const unexpectedMessage = (status: number) =>
  `Unexpected response from the server (status ${status}). Reload the page and try again.`;

class RequestError extends Error {}

/**
 * Fetches and parses JSON without ever throwing on a bad body. HTML error
 * pages, empty bodies and Devise 401s all come back as a result the caller
 * turns into a readable message. Only a failed network round trip throws.
 */
async function requestJson(url: string, init: RequestInit): Promise<JsonResult> {
  let response: Response;
  try {
    response = await fetch(url, { credentials: 'same-origin', ...init });
  } catch {
    throw new RequestError(NETWORK_MESSAGE);
  }

  let data: JsonRecord | null = null;
  try {
    const text = await response.text();
    const parsed: unknown = text ? JSON.parse(text) : null;
    data = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as JsonRecord) : null;
  } catch {
    data = null;
  }

  return { ok: response.ok, status: response.status, redirected: Boolean(response.redirected), data };
}

const stringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

function failureMessage(result: JsonResult, messageKey: 'error' | 'message'): string {
  if (result.status === 401) return SESSION_MESSAGE;
  const serverMessage = result.data ? stringOrNull(result.data[messageKey]) : null;
  if (serverMessage) return serverMessage;
  if (result.status === 403) return FORBIDDEN_MESSAGE;
  if (!result.data && result.redirected) return REDIRECT_MESSAGE;
  if (!result.data) return unexpectedMessage(result.status);
  return `Request failed (status ${result.status}). Try again.`;
}

const errorText = (err: unknown): string =>
  err instanceof RequestError ? err.message : 'Something went wrong. Reload the page and try again.';

const withParams = (url: string, params: Record<string, string | number>): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => query.append(key, String(value)));
  return `${url}${url.includes('?') ? '&' : '?'}${query.toString()}`;
};

const dashboardUrlFor = (customerId: string) =>
  `${DASHBOARD_PREFIX}customers/${encodeURIComponent(customerId)}`;

function normalizeSubscription(raw: unknown): StripeSubscriptionSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as JsonRecord;
  const id = stringOrNull(r.id);
  if (!id) return null;
  return {
    id,
    status: stringOrNull(r.status) || 'unknown',
    price_id: stringOrNull(r.price_id),
    price_nickname: stringOrNull(r.price_nickname),
    current_period_end: stringOrNull(r.current_period_end),
  };
}

function normalizeLinkedUser(raw: unknown): StripeLinkedUser | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as JsonRecord;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const adminUrl = stringOrNull(r.admin_url);
  return {
    id,
    email: stringOrNull(r.email) || `User #${id}`,
    // Only follow app-relative links; never render a server string as an arbitrary href.
    admin_url: adminUrl && adminUrl.startsWith('/') && !adminUrl.startsWith('//') ? adminUrl : `/admin/users/${id}`,
  };
}

function normalizeCustomer(raw: unknown): StripeCustomerSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as JsonRecord;
  const id = stringOrNull(r.id);
  if (!id) return null;
  const dashboardUrl = stringOrNull(r.dashboard_url);
  return {
    id,
    email: stringOrNull(r.email),
    name: stringOrNull(r.name),
    created_at: stringOrNull(r.created_at),
    dashboard_url: dashboardUrl && dashboardUrl.startsWith(DASHBOARD_PREFIX) ? dashboardUrl : dashboardUrlFor(id),
    subscriptions: Array.isArray(r.subscriptions)
      ? r.subscriptions.map(normalizeSubscription).filter((s): s is StripeSubscriptionSummary => s !== null)
      : [],
    linked_user: normalizeLinkedUser(r.linked_user),
    current: r.current === true,
  };
}

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'bg-success';
    case 'past_due':
    case 'unpaid':
    case 'incomplete':
    case 'paused':
      return 'bg-warning text-dark';
    default:
      return 'bg-secondary';
  }
};

const SubscriptionList: React.FC<{ subscriptions: StripeSubscriptionSummary[] }> = ({ subscriptions }) => {
  if (subscriptions.length === 0) {
    return <span className="text-muted small">No subscriptions</span>;
  }
  return (
    <ul className="list-unstyled mb-0 small d-grid gap-1">
      {subscriptions.map((sub) => {
        const periodEnd = formatDate(sub.current_period_end);
        return (
          <li key={sub.id} className="d-flex flex-wrap align-items-center gap-2">
            <span className={`badge ${statusBadgeClass(sub.status)}`}>{sub.status.replace(/_/g, ' ')}</span>
            <span className="text-break">{sub.price_nickname || sub.price_id || 'Unknown price'}</span>
            {periodEnd && <span className="text-muted">Period ends {periodEnd}</span>}
          </li>
        );
      })}
    </ul>
  );
};

const DetailPair: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="detail-pair">
    <span className="detail-pair__label">{label}</span>
    <span className="detail-pair__value text-break">{children}</span>
  </div>
);

const NotSet: React.FC = () => <span className="text-muted">Not set</span>;

/**
 * StripeCustomerLinker - view and fix which Stripe customer a user bills through.
 *
 * - Loads the linked customer after the page renders (no Stripe call in the Rails render)
 * - Searches Stripe by email, name, or a pasted cus_ id (search starts prefilled, never automatically)
 * - Links a result to the user after an inline confirmation, then refreshes the linked customer
 * - Every request failure (network, non-OK status, non-JSON body, Devise 401) shows a readable message
 */
export const StripeCustomerLinker: React.FC<StripeCustomerLinkerProps> = ({
  userId,
  userEmail,
  context,
  searchUrl,
  currentUrl,
  linkUrl,
  csrfToken,
}) => {
  const [current, setCurrent] = useState<CurrentState>({ status: 'loading' });
  const [query, setQuery] = useState(userEmail || '');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<StripeCustomerSummary[] | null>(null);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [linkFeedback, setLinkFeedback] = useState<LinkFeedback | null>(null);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);

  const mountedRef = useRef(true);
  const currentSeq = useRef(0);
  const searchSeq = useRef(0);
  // Read synchronously by handleSearch: pressing Enter in the search box can
  // submit the form even while the Search button is disabled for a link.
  const linkingRef = useRef(false);
  const resultItems = useRef(new Map<string, HTMLLIElement>());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Opening, cancelling or finishing a confirmation unmounts the focused
  // control. Move focus to its replacement once that has rendered, so
  // keyboard and screen reader users do not land back on <body>.
  useEffect(() => {
    if (!focusRequest) return;
    const item = resultItems.current.get(focusRequest.customerId);
    item?.querySelector<HTMLElement>(`[data-focus-target="${focusRequest.target}"]`)?.focus();
    setFocusRequest(null);
  }, [focusRequest]);

  const loadCurrent = useCallback(async () => {
    const seq = ++currentSeq.current;
    const isLatest = () => mountedRef.current && seq === currentSeq.current;
    setCurrent({ status: 'loading' });

    try {
      const result = await requestJson(withParams(currentUrl, { user_id: userId }), {
        headers: { Accept: 'application/json' },
      });
      if (!isLatest()) return;

      if (!result.ok) {
        setCurrent({ status: 'error', message: failureMessage(result, 'error') });
        return;
      }
      if (!result.data || !('stripe_id' in result.data)) {
        setCurrent({ status: 'error', message: unexpectedMessage(result.status) });
        return;
      }

      const customer = normalizeCustomer(result.data.customer);
      setCurrent({
        status: 'loaded',
        stripeId: stringOrNull(result.data.stripe_id) || customer?.id || null,
        customer,
        note: stringOrNull(result.data.error),
      });
    } catch (err) {
      if (isLatest()) setCurrent({ status: 'error', message: errorText(err) });
    }
  }, [currentUrl, userId]);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  const handleSearch = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // A search that lands mid-link would replace the list with `current`
      // flags computed before the link committed, and could unmount the row
      // that shows the link result. Searching waits until the link settles.
      if (linkingRef.current) return;
      const q = query.trim();
      setLinkFeedback(null);
      setConfirmingId(null);

      if (!q) {
        searchSeq.current += 1;
        setSearching(false);
        setResults(null);
        setSearchError(BLANK_QUERY_MESSAGE);
        return;
      }

      const seq = ++searchSeq.current;
      const isLatest = () => mountedRef.current && seq === searchSeq.current;
      setSearching(true);
      setSearchError(null);

      try {
        const result = await requestJson(withParams(searchUrl, { q, user_id: userId }), {
          headers: { Accept: 'application/json' },
        });
        if (!isLatest()) return;

        if (!result.ok) {
          setResults(null);
          setSearchError(failureMessage(result, 'error'));
          return;
        }
        if (!result.data || !Array.isArray(result.data.customers)) {
          setResults(null);
          setSearchError(unexpectedMessage(result.status));
          return;
        }

        setResults(
          result.data.customers
            .map(normalizeCustomer)
            .filter((c): c is StripeCustomerSummary => c !== null)
        );
        setSearchedQuery(q);
      } catch (err) {
        if (!isLatest()) return;
        setResults(null);
        setSearchError(errorText(err));
      } finally {
        if (isLatest()) setSearching(false);
      }
    },
    [query, searchUrl, userId]
  );

  const linkCustomer = useCallback(
    async (customer: StripeCustomerSummary) => {
      if (linkingRef.current) return;
      linkingRef.current = true;
      setLinkingId(customer.id);
      setLinkFeedback(null);

      const fail = (text: string) => setLinkFeedback({ customerId: customer.id, variant: 'danger', text });

      try {
        const result = await requestJson(linkUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({ user_id: userId, customer_id: customer.id, context }),
        });
        if (!mountedRef.current) return;

        if (!result.ok) {
          fail(failureMessage(result, 'message'));
          return;
        }
        if (!result.data) {
          fail(unexpectedMessage(result.status));
          return;
        }
        if (result.data.success !== true) {
          fail(stringOrNull(result.data.message) || 'The link was not saved. Try again.');
          return;
        }

        const linkedId = stringOrNull(result.data.stripe_id) || customer.id;
        setResults((prev) => (prev ? prev.map((c) => ({ ...c, current: c.id === linkedId })) : prev));
        setLinkFeedback({
          customerId: customer.id,
          variant: 'success',
          text: stringOrNull(result.data.message) || `Linked ${linkedId} to this user.`,
        });
        loadCurrent();
      } catch (err) {
        if (mountedRef.current) fail(errorText(err));
      } finally {
        linkingRef.current = false;
        if (mountedRef.current) {
          setLinkingId(null);
          setConfirmingId(null);
          // The confirm panel is gone; the success or error message replaces it.
          setFocusRequest({ customerId: customer.id, target: 'feedback' });
        }
      }
    },
    [linkUrl, csrfToken, userId, context, loadCurrent]
  );

  const isLinking = linkingId !== null;
  const currentStripeId = current.status === 'loaded' ? current.stripeId : null;
  const inputId = `stripe-customer-search-${userId}`;
  const helpId = `${inputId}-help`;

  const renderCurrent = () => {
    if (current.status === 'loading') {
      return (
        <div className="d-flex align-items-center gap-2 text-muted small" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          Loading the linked Stripe customer...
        </div>
      );
    }

    if (current.status === 'error') {
      return (
        <div className="alert alert-danger d-flex flex-wrap align-items-center justify-content-between gap-2 mx-0 mb-0 small shadow-none" role="alert">
          <span>{current.message}</span>
          <Button size="sm" variant="danger" outline onClick={() => loadCurrent()}>
            Retry
          </Button>
        </div>
      );
    }

    if (!current.stripeId) {
      return <p className="text-muted small mb-0">No Stripe customer linked</p>;
    }

    const { customer } = current;
    const created = formatDate(customer?.created_at ?? null);
    return (
      <div data-testid="stripe-current-customer">
        {current.note && <div className="alert alert-warning small py-2 mx-0 mb-3 shadow-none">{current.note}</div>}
        <div className="row">
          <div className={customer ? 'col-md-6 mb-3 mb-md-0' : 'col-md-6'}>
            <DetailPair label="Customer ID">
              <a
                href={customer?.dashboard_url || dashboardUrlFor(current.stripeId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <code>{current.stripeId}</code> <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
              </a>
            </DetailPair>
            {customer && (
              <>
                <DetailPair label="Email">{customer.email || <NotSet />}</DetailPair>
                <DetailPair label="Name">{customer.name || <NotSet />}</DetailPair>
              </>
            )}
          </div>
          {customer && (
            <div className="col-md-6">
              <DetailPair label="Created">{created || <NotSet />}</DetailPair>
              <DetailPair label="Subscriptions">
                <SubscriptionList subscriptions={customer.subscriptions} />
              </DetailPair>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderResult = (customer: StripeCustomerSummary) => {
    const created = formatDate(customer.created_at);
    const otherUser =
      customer.linked_user && customer.linked_user.id !== userId ? customer.linked_user : null;
    const confirming = confirmingId === customer.id;
    const linkingThis = linkingId === customer.id;
    const feedback = linkFeedback && linkFeedback.customerId === customer.id ? linkFeedback : null;
    const replacesId = currentStripeId && currentStripeId !== customer.id ? currentStripeId : null;
    const confirmPromptId = `stripe-confirm-${userId}-${customer.id}`;
    const confirmReplacesId = `${confirmPromptId}-replaces`;

    return (
      <li
        key={customer.id}
        ref={(el) => {
          if (el) resultItems.current.set(customer.id, el);
          else resultItems.current.delete(customer.id);
        }}
        className="list-group-item"
        data-testid="stripe-search-result"
      >
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div className="flex-grow-1 text-break overflow-hidden">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <a href={customer.dashboard_url} target="_blank" rel="noopener noreferrer">
                <code>{customer.id}</code>
              </a>
              {customer.current && <span className="badge bg-success">Current</span>}
            </div>
            <div className="small">{customer.email || <span className="text-muted">No email</span>}</div>
            {customer.name && <div className="small">{customer.name}</div>}
            {created && <div className="small text-muted">Created {created}</div>}
          </div>
          {!customer.current && !confirming && (
            <Button
              size="sm"
              variant="primary"
              outline
              // While a search runs these rows are about to be replaced, and
              // their Current flags may be stale; linking waits for fresh results.
              disabled={isLinking || searching || otherUser !== null}
              aria-label={`Link to this user: ${customer.id}`}
              data-focus-target="link"
              onClick={() => {
                setLinkFeedback(null);
                setConfirmingId(customer.id);
                setFocusRequest({ customerId: customer.id, target: 'confirm' });
              }}
            >
              Link to this user
            </Button>
          )}
        </div>

        <div className="mt-2">
          <SubscriptionList subscriptions={customer.subscriptions} />
        </div>

        {otherUser && (
          <div className="small mt-2 text-danger text-break">
            <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
            Already linked to another user:{' '}
            <a href={otherUser.admin_url}>
              #{otherUser.id} {otherUser.email}
            </a>
          </div>
        )}

        {confirming && !customer.current && (
          <div
            className="border rounded bg-light p-3 mt-2"
            role="group"
            aria-labelledby={confirmPromptId}
            aria-describedby={replacesId ? confirmReplacesId : undefined}
            tabIndex={-1}
            data-focus-target="confirm"
          >
            <div id={confirmPromptId} className="small text-break mb-2">
              Link <code>{customer.id}</code> to {userEmail || 'this user'}?
            </div>
            {replacesId && (
              <div id={confirmReplacesId} className="small text-muted text-break mb-2">
                This replaces {replacesId}.
              </div>
            )}
            <div className="d-flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="primary"
                loading={linkingThis}
                disabled={isLinking || searching}
                onClick={() => linkCustomer(customer)}
              >
                {linkingThis ? 'Linking...' : 'Confirm link'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                outline
                disabled={isLinking}
                onClick={() => {
                  setConfirmingId(null);
                  setFocusRequest({ customerId: customer.id, target: 'link' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {feedback && (
          <div
            className={`alert alert-${feedback.variant} small text-break py-2 mx-0 mt-2 mb-0 shadow-none`}
            role={feedback.variant === 'success' ? 'status' : 'alert'}
            tabIndex={-1}
            data-focus-target="feedback"
          >
            {feedback.text}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="stripe-customer-linker">
      <section className="mb-3" aria-label="Linked Stripe customer">
        <div className="small fw-semibold mb-2">Linked customer</div>
        {renderCurrent()}
      </section>

      <section className="border-top pt-3" aria-label="Find a Stripe customer">
        <form role="search" className="mb-3" onSubmit={handleSearch}>
          <label htmlFor={inputId} className="form-label small fw-semibold">
            Search Stripe customers
          </label>
          <div className="input-group input-group-sm">
            <input
              id={inputId}
              type="search"
              className="form-control mb-0"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Email, name, or cus_..."
              aria-describedby={helpId}
              autoComplete="off"
            />
            <Button type="submit" size="sm" variant="primary" loading={searching} disabled={isLinking}>
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </div>
          <div id={helpId} className="form-text">
            Search by email or name, or paste a customer ID that starts with cus_.
          </div>
        </form>

        {searchError && (
          <div className="alert alert-danger small text-break py-2 mx-0 mb-3 shadow-none" role="alert">
            {searchError}
          </div>
        )}

        {results && results.length === 0 && !searchError && (
          <p className="text-muted small text-break mb-0">
            No Stripe customers found for <strong>{searchedQuery}</strong>.
          </p>
        )}

        {results && results.length > 0 && (
          <ul className="list-group" aria-label="Stripe customers" aria-busy={searching}>
            {results.map(renderResult)}
          </ul>
        )}
      </section>
    </div>
  );
};

export default StripeCustomerLinker;
